import { useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { captureAttributionFromSearch } from "@/lib/analytics/attributionCapture";
import {
  getPersistedFirstTouchAttribution,
  getPersistedLastTouchAttribution,
} from "@/lib/analytics/attributionCookies";
import { useTRPCClient } from "@/lib/trpc";

/**
 * Relays an immutable attribution snapshot to the API on navigations carrying tracking params
 * (utm / gclid / msclkid / gbraid / wbraid / fbclid), and on a new visit from another site (its
 * referrer alone). The API persists the canonical DB touch but never
 * echoes these cookies: a delayed HTTP response must not restore attribution across a later
 * account boundary.
 *
 * Mounted in App inside Providers because it needs the tRPC client. Capture is idempotent and
 * fire-and-forget.
 */
export const AttributionRelay = () => {
  const trpcClient = useTRPCClient();
  const [location] = useLocation();
  const search = useSearch();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!captureAttributionFromSearch(window.location.search)) return;

    const last = getPersistedLastTouchAttribution();
    if (!last) return; // cookie write failed (storage blocked) — nothing to relay

    const first = getPersistedFirstTouchAttribution();
    void trpcClient.marketing.captureAttribution.mutate({ ...(first ? { first } : {}), last }).catch(() => {
      // Deliberately swallowed — first-party analytics persistence is never user-visible.
    });
  }, [location, search, trpcClient]);

  return null;
};
